/**
 * CR154.2: Compare Sizes Preview Component
 * 
 * Shows all 3 card sizes (compact/normal/large) side-by-side with their
 * respective fold lines for visual comparison.
 */

import { useMemo, memo } from "react";
import { DealGiftCard } from "@/components/deals/DealGiftCard";
import { DealCardUXProvider } from "@/contexts/DealCardUXContext";
import { GlobalThemeProvider } from "@/providers/GlobalThemeProvider";
import { Badge } from "@/components/ui/badge";
import type { Deal, DealCategory } from "@/lib/types/deals";
import type { DealTypeRegistryEntry } from "@/hooks/useDealCardConfig";
import type { GlobalThemeTokens } from "@/lib/deals/globalThemeTokens";
import type { CardSize } from "@/lib/layout-studio/dealCardUXTypes";
import { useFoldLineSettings, mapCardSizeToFoldKey } from "@/hooks/useFoldLineSettings";
import { PreviewErrorBoundary } from "@/components/admin/preview";

// ================================================================
// SAMPLE DEALS
// ================================================================

const SAMPLE_DEAL: Partial<Deal> = {
  merchant: "Shoppers Drug Mart",
  promo_merchant_name: "Shoppers Drug Mart",
  promo_merchant_key: "shoppers-drug-mart",
  reward_brand_name: "The Keg",
  reward_brand_key: "the-keg",
  reward_type: "gift_card",
  qualifying_product_name: "The Keg gift card",
  validation_flags: [],
  purchase_gift_card_brand: "The Keg",
  gift_card_brand: "The Keg",
  bonus_merchant: "The Keg",
  bonus_amount: 25,
  spend_amount: 100,
  bonus_or_discount_amount: 25,
  headline_mechanic: "Buy $100 The Keg, get $25 bonus",
  deal_category: "BONUS",
  deal_type: "bonus",
  start_date: new Date().toISOString(),
  end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
  source_url: "https://example.com",
  source_type: "flyer",
  confidence: "High",
  availability_scope: "all_locations",
  computed_value_cad: 25,
  effective_percent_back: 25,
};

// ================================================================
// SINGLE SIZE PREVIEW (memoized for stability)
// ================================================================

interface SingleSizePreviewProps {
  cardSize: CardSize;
  foldHeightPx: number;
  showFoldLine: boolean;
  dealType: string;
  registry?: DealTypeRegistryEntry;
  template?: Record<string, unknown> | null;
  globalThemeOverride?: GlobalThemeTokens | null;
}

const SingleSizePreview = memo(function SingleSizePreview({
  cardSize,
  foldHeightPx,
  showFoldLine,
  dealType,
  registry,
  template,
  globalThemeOverride,
}: SingleSizePreviewProps) {
  const sampleDeal = useMemo(() => ({
    ...SAMPLE_DEAL,
    deal_category: dealType as DealCategory,
    deal_type: dealType.toLowerCase() as Deal["deal_type"],
  } as Deal), [dealType]);

  const registryOverride = useMemo(() => {
    if (!registry) return null;
    return {
      ribbon_text: registry.ribbon_text ?? "",
      ribbon_theme: registry.ribbon_theme ?? "blue",
      is_enabled: registry.is_enabled ?? true,
      label: registry.label ?? dealType,
    };
  }, [registry, dealType]);

  const templateOverride = useMemo(() => {
    if (!template) return null;
    return template as {
      sections?: Record<string, boolean>;
      labels?: Record<string, string>;
      header?: Record<string, unknown>;
      extended_sections?: Record<string, boolean>;
      styling?: Record<string, unknown>;
      section_order?: string[];
      theme_preset?: "light" | "dark" | "custom";
    };
  }, [template]);

  // Map cardSize to display label
  const sizeLabel = cardSize === "compact" ? "Compact" : cardSize === "regular" ? "Normal" : "Large";
  const heightPx = cardSize === "compact" ? 200 : cardSize === "regular" ? 400 : 600;

  // Scale factor for fitting cards in compare view
  const scale = 0.65;
  const scaledFoldHeight = foldHeightPx * scale;

  const cardContent = (
    <DealCardUXProvider cardSize={cardSize} density="comfortable">
      <DealGiftCard
        deal={sampleDeal}
        isAdmin={true}
        registryOverride={registryOverride}
        templateOverride={templateOverride}
      />
    </DealCardUXProvider>
  );

  return (
    <div className="flex flex-col gap-2">
      {/* Size label */}
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="text-xs">
          {sizeLabel}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {heightPx}px
        </span>
      </div>

      {/* Card container */}
      <div 
        className="relative border rounded-lg bg-muted/20 overflow-visible"
        style={{ 
          minHeight: `${(heightPx * scale) + 80}px`,
          padding: "8px",
        }}
      >
        <PreviewErrorBoundary previewContext={`Compare-${sizeLabel}`}>
          <div 
            className="relative overflow-visible origin-top-left"
            style={{ 
              transform: `scale(${scale})`,
              width: `${100 / scale}%`,
            }}
          >
            {globalThemeOverride ? (
              <GlobalThemeProvider overrideTokens={globalThemeOverride}>
                {cardContent}
              </GlobalThemeProvider>
            ) : (
              cardContent
            )}
          </div>

          {/* Fold line overlay */}
          {showFoldLine && (
            <div 
              className="absolute left-0 right-0 pointer-events-none z-30"
              style={{ top: `${scaledFoldHeight + 8}px` }}
            >
              <div className="w-full border-t-2 border-dashed border-orange-500/70" />
              <div className="absolute right-1 -top-4">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/50 border border-orange-300 dark:border-orange-700 text-xs font-medium text-orange-700 dark:text-orange-300">
                  {foldHeightPx}px
                </span>
              </div>
            </div>
          )}
        </PreviewErrorBoundary>
      </div>
    </div>
  );
});

// ================================================================
// MAIN COMPONENT
// ================================================================

interface CompareSizesPreviewProps {
  dealType: string;
  registry?: DealTypeRegistryEntry;
  template?: Record<string, unknown> | null;
  globalThemeOverride?: GlobalThemeTokens | null;
}

export function CompareSizesPreview({
  dealType,
  registry,
  template,
  globalThemeOverride,
}: CompareSizesPreviewProps) {
  const { settings } = useFoldLineSettings();

  const sizes: CardSize[] = ["compact", "regular", "large"];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Compare all card sizes with fold lines</span>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {sizes.map((size) => {
          const foldKey = mapCardSizeToFoldKey(size);
          const foldHeight = settings.heights[foldKey];

          return (
            <SingleSizePreview
              key={size}
              cardSize={size}
              foldHeightPx={foldHeight}
              showFoldLine={settings.enabled}
              dealType={dealType}
              registry={registry}
              template={template}
              globalThemeOverride={globalThemeOverride}
            />
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground italic">
        Cards scaled to 65% for comparison. Fold lines show configured heights.
      </p>
    </div>
  );
}

export default CompareSizesPreview;
